import { useState } from 'react';
import { Box, Flex, Heading, Tab, TabList, TabPanel, TabPanels, Tabs, VStack } from '@chakra-ui/react';
import { AI_LEAGUE_TABS } from '@/constants/aiLeague/aiLeagueTabs';
import type { AiModelId } from '@/constants/aiLeague/aiLeagueTypes';
import { getAiModelTabIndex, getMethodologyTabIndex } from '@/constants/aiLeague/aiLeagueTabs';
import { useAiLeaguePageData } from '@/hooks/useAiLeaguePageData';
import type { gameResult } from '@/models/gameUpdate';
import { AiLeagueMethodologyPanel } from './AiLeagueMethodologyPanel';
import { AiLeagueMobileTabBar } from './AiLeagueMobileTabBar';
import { AiLeagueTabPanel } from './AiLeagueTabPanel';
import { AiModelTabPanel } from './AiModelTabPanel';

export function AiLeaguePageContent(props: { gameResults: gameResult[] }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [modelsMenuOpen, setModelsMenuOpen] = useState(false);
  const pageData = useAiLeaguePageData(props.gameResults);

  function selectTab(index: number) {
    setTabIndex(index);
    setModelsMenuOpen(false);
  }

  function handleModelClick(modelId: AiModelId) {
    selectTab(getAiModelTabIndex(modelId));
  }

  function handleMethodologyClick() {
    selectTab(getMethodologyTabIndex());
  }

  function handleSelectModel(modelId: AiModelId) {
    selectTab(getAiModelTabIndex(modelId));
  }

  const [firstTab, ...restTabs] = AI_LEAGUE_TABS;
  const modelTabs = restTabs.slice(0, -1);
  const methodologyTab = restTabs.at(-1)!;

  return (
    <VStack align='stretch' spacing={8} w='full'>
      <Heading as='h1' fontSize={{ base: 'xl', md: '2xl' }} fontWeight={700}>
        AI League
      </Heading>

      <Tabs index={tabIndex} onChange={selectTab} variant='line' colorScheme='blue' w='full'>
        <Box display={{ base: 'block', lg: 'none' }} w='full'>
          <AiLeagueMobileTabBar
            tabIndex={tabIndex}
            aiLeagueTab={firstTab}
            modelTabs={modelTabs}
            methodologyTab={methodologyTab}
            modelsMenuOpen={modelsMenuOpen}
            onToggleModelsMenu={() => setModelsMenuOpen((open) => !open)}
            onSelectTab={selectTab}
            onSelectModel={handleSelectModel}
          />
        </Box>
        <TabList
          w='full'
          display={{ base: 'none', lg: 'flex' }}
          alignItems='flex-end'
          flexWrap='wrap'
          gap={1}
        >
          <Tab flexShrink={0} fontSize={{ base: 'sm', md: 'md' }}>
            {firstTab.label}
          </Tab>
          <Flex flex={1} justify='center' flexWrap='wrap' gap={1} minW={0}>
            {modelTabs.map((tab) => (
              <Tab key={tab.id} fontSize={{ base: 'sm', md: 'md' }}>
                {tab.label}
              </Tab>
            ))}
          </Flex>
          <Tab flexShrink={0} fontSize={{ base: 'sm', md: 'md' }}>
            {methodologyTab.label}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt={4}>
            <AiLeagueTabPanel
              entries={pageData.leaderboardEntries}
              getMemberForRow={pageData.getMemberForRow}
              tournamentStarted={pageData.tournamentStarted}
              settledMatchday={pageData.settledMatchday}
              chartUser={pageData.chartUser}
              aiModels={pageData.aiModels}
              gameResults={props.gameResults}
              leader={pageData.leader}
              userYouLine={pageData.userYouLine}
              userDisplayName={pageData.userDisplayName}
              isLoggedIn={pageData.isLoggedIn}
              hasUserList={pageData.hasUserList}
              onModelClick={handleModelClick}
              onMethodologyClick={handleMethodologyClick}
            />
          </TabPanel>

          {pageData.aiModels.map((model) => (
            <TabPanel key={model.id} px={0} pt={4}>
              {tabIndex === getAiModelTabIndex(model.id) ? (
                <AiModelTabPanel
                  model={model}
                  tournamentStarted={pageData.tournamentStarted}
                  leaderboardEntries={pageData.leaderboardEntries}
                />
              ) : null}
            </TabPanel>
          ))}

          <TabPanel px={0} pt={{ base: 8, md: 10 }}>
            {tabIndex === AI_LEAGUE_TABS.length - 1 ? <AiLeagueMethodologyPanel /> : null}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
